// Stillhouse view — Settings › Updates. Shows the running version and checks
// GitHub Releases for a newer one, with its changelog.
import React, { useCallback, useEffect, useState } from "react";
import { C, F, Pill, Btn } from "../brand/apo";
import { Card, SectionLabel, PageHeader, COPPER } from "../brand/sh";
import { api, type UpdateInfo } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

export function Updates() {
  const [info, setInfo] = useState<UpdateInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInfo(await api.checkUpdates());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  const updateAvailable = info?.updateAvailable === true;
  // status pill
  let pill: React.ReactNode = null;
  if (loading) pill = <Pill color={C.fgMute} size="sm">Checking…</Pill>;
  else if (info?.error || error) pill = <Pill color={C.amber} size="sm">Couldn't check</Pill>;
  else if (updateAvailable) pill = <Pill color={C.glow} size="sm" glow>Update available</Pill>;
  else pill = <Pill color={C.glowDp} size="sm">Up to date</Pill>;

  return (
    <React.Fragment>
      <PageHeader crumb="SETTINGS › UPDATES" title="Updates" sub="The running Stillhouse version, and new releases from GitHub.">
        <Btn primary onClick={() => void check()}>
          {loading ? "Checking…" : "Check for updates"}
        </Btn>
      </PageHeader>
      <Scroll>
        <Card>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 28, flexWrap: "wrap" }}>
            <div>
              <SectionLabel>Current version</SectionLabel>
              <div style={{ fontFamily: F.display, fontWeight: 500, fontSize: 44, color: C.fgDark, letterSpacing: "-0.03em", lineHeight: 1, marginTop: 8 }}>
                {info?.currentVersion ?? "—"}
              </div>
            </div>
            <div style={{ paddingBottom: 6 }}>{pill}</div>
            {info?.latestVersion && updateAvailable && (
              <div style={{ paddingBottom: 4 }}>
                <SectionLabel>Latest</SectionLabel>
                <div style={{ fontFamily: F.mono, fontSize: 18, color: C.glow, fontWeight: 700, marginTop: 8 }}>{info.latestVersion}</div>
              </div>
            )}
            <div style={{ flex: 1 }} />
            {info?.checkedAt && (
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint, paddingBottom: 8 }}>
                Checked {new Date(info.checkedAt).toLocaleString()}
              </div>
            )}
          </div>

          {(info?.note || info?.error || error) && (
            <div style={{ marginTop: 16, padding: "10px 14px", background: C.inkHi, border: `1px solid ${C.line}`, fontFamily: F.mono, fontSize: 12, color: info?.error || error ? C.amber : C.fgMute, lineHeight: 1.5 }}>
              {info?.error || error || info?.note}
            </div>
          )}
        </Card>

        {updateAvailable && (
          <Card
            title={info?.releaseName || `Stillhouse ${info?.latestVersion}`}
            tag={<Pill color={COPPER} size="sm">{info?.latestVersion}</Pill>}
            action={
              info?.releaseUrl ? (
                <a href={info.releaseUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                  <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12 }}>
                    View release ↗
                  </Btn>
                </a>
              ) : null
            }
          >
            <SectionLabel>Changelog</SectionLabel>
            <pre style={{ marginTop: 10, fontFamily: F.mono, fontSize: 12.5, color: C.fgDark, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 360, overflow: "auto" }}>
              {info?.changelog?.trim() || "No release notes."}
            </pre>
            {info?.publishedAt && (
              <div style={{ marginTop: 12, fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>Published {new Date(info.publishedAt).toLocaleDateString()}</div>
            )}
          </Card>
        )}
      </Scroll>
    </React.Fragment>
  );
}
